/* global.js — site chrome behaviour (vanilla, deferred).
   Drawers (mobile nav + cart), mega-menu keyboard support, sticky header,
   generic disclosures. Exposes window.theme.drawers for cart.js. */
(function () {
  'use strict';
  var theme = (window.theme = window.theme || {});

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ---------------- Drawer manager ---------------- */
  var lastFocus = null;

  function openDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('drawer-open');
    // reflect on any toggles
    document.querySelectorAll('[data-drawer-toggle="' + id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'true');
    });
    var panel = drawer.querySelector('.drawer__panel');
    var first = panel && panel.querySelector(FOCUSABLE);
    if (first) window.requestAnimationFrame(function () { first.focus(); });
    drawer.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  function closeDrawer(drawer) {
    if (typeof drawer === 'string') drawer = document.getElementById(drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    if (!document.querySelector('.drawer[aria-hidden="false"]')) {
      document.body.classList.remove('drawer-open');
    }
    document.querySelectorAll('[data-drawer-toggle="' + drawer.id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', 'false');
    });
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    drawer.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  function closeAllDrawers() {
    document.querySelectorAll('.drawer[aria-hidden="false"]').forEach(closeDrawer);
  }

  theme.drawers = { open: openDrawer, close: closeDrawer, closeAll: closeAllDrawers };

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-drawer-toggle]');
    if (toggle) {
      e.preventDefault();
      openDrawer(toggle.getAttribute('data-drawer-toggle'));
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.closest('.drawer'));
    }
  });

  /* Focus trap + Escape */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      closeAllDrawers();
      // close any open mega panel by releasing focus
      var openMega = document.querySelector('.mega-menu.is-open');
      if (openMega) {
        openMega.classList.remove('is-open');
        var parentLink = openMega.parentElement.querySelector('[data-mega-toggle]');
        if (parentLink) parentLink.setAttribute('aria-expanded', 'false');
      }
      if (document.activeElement && document.activeElement.closest('.has-mega')) {
        document.activeElement.blur();
      }
      return;
    }
    if (e.key === 'Tab') {
      var drawer = document.querySelector('.drawer[aria-hidden="false"]');
      if (!drawer) return;
      var panel = drawer.querySelector('.drawer__panel');
      var nodes = Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE)).filter(function (n) {
        return n.offsetParent !== null;
      });
      if (!nodes.length) return;
      var firstEl = nodes[0], lastEl = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    }
  });

  /* ---------------- Mega-menu (touch / click fallback) ----------------
     Desktop opens on hover/focus via CSS. This adds explicit toggling so
     touch + keyboard users can open without navigating away immediately. */
  document.querySelectorAll('[data-mega-toggle]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      // Only intercept on coarse pointers (touch); fine pointers use hover.
      if (window.matchMedia('(hover: none)').matches && window.innerWidth >= 961) {
        var panel = link.parentElement.querySelector('[data-mega-panel]');
        if (panel && !panel.classList.contains('is-open')) {
          e.preventDefault();
          document.querySelectorAll('.mega-menu.is-open').forEach(function (p) { p.classList.remove('is-open'); });
          panel.classList.add('is-open');
          link.setAttribute('aria-expanded', 'true');
        }
      }
    });
  });
  document.addEventListener('click', function (e) {
    if (!e.target.closest('.has-mega')) {
      document.querySelectorAll('.mega-menu.is-open').forEach(function (p) {
        p.classList.remove('is-open');
        var l = p.parentElement.querySelector('[data-mega-toggle]');
        if (l) l.setAttribute('aria-expanded', 'false');
      });
    }
  });

  /* ---------------- Sticky header: shadow + auto-hide ----------------
     Adds the drop-shadow once scrolled, and on mobile hides the header when
     scrolling down / reveals it when scrolling up (a "reverse sticky" so the
     menu is out of the way while reading but one flick away). Desktop keeps the
     header pinned — the auto-hide only arms under the mobile media query. */
  var header = document.querySelector('[data-header].header--sticky');
  if (header) {
    var lastY = window.scrollY;
    var mobile = window.matchMedia('(max-width: 989px)');
    // Don't hide until the user is clearly past the header, so it can't flicker
    // near the top of the page.
    var revealAbove = 140;

    var onScroll = function () {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);

      var goingDown = y > lastY;
      // Keep the header visible near the top, while any drawer is open, or on desktop.
      if (!mobile.matches || document.body.classList.contains('drawer-open') || y < revealAbove) {
        header.classList.remove('header--tucked');
      } else if (goingDown && y - lastY > 4) {
        header.classList.add('header--tucked');
      } else if (!goingDown && lastY - y > 4) {
        header.classList.remove('header--tucked');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // A resize into desktop must clear any tucked state.
    mobile.addEventListener('change', function () { if (!mobile.matches) header.classList.remove('header--tucked'); });
    onScroll();
  }

  /* ---------------- Generic disclosure (accordions) ----------------
     <button data-toggle aria-controls="ID" aria-expanded="false"> ... <div id="ID"> */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-toggle]');
    if (!trigger) return;
    var id = trigger.getAttribute('aria-controls');
    var panel = id && document.getElementById(id);
    if (!panel) return;
    var expanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', String(!expanded));
    panel.hidden = expanded;
  });

  /* ---------------- Wishlist (lightweight localStorage toggle) ---------------- */
  try {
    var WISH_KEY = 'coa:wishlist';
    var wish = JSON.parse(localStorage.getItem(WISH_KEY) || '[]');
    var paintWish = function () {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
        var on = wish.indexOf(btn.getAttribute('data-product-id')) !== -1;
        btn.setAttribute('aria-pressed', String(on));
        btn.classList.toggle('is-active', on);
      });
    };
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-product-id');
      var i = wish.indexOf(id);
      if (i === -1) { wish.push(id); } else { wish.splice(i, 1); }
      localStorage.setItem(WISH_KEY, JSON.stringify(wish));
      paintWish();
    });
    document.addEventListener('cart:updated', paintWish);
    paintWish();
  } catch (e) {}
})();
